// Reads the server's "this save would change the building's time zone" answer.
//
// PUT /dashboard/buildings/edit-property refuses a zone change with
// 409 { code: 'ZONE_CHANGE_NEEDS_CONFIRM', message, timezone: { from, to },
// impact: { live_passes } } until the request carries confirm_zone_change=1.
// This turns that body into the sentence the confirm dialog shows.
//
// 409 { code: 'SHIFT_OPEN' } is deliberately NOT handled here: there is nothing
// to confirm — the shift has to be closed first — so it falls through to the
// form's ordinary error message. The server sends that only for a building
// whose zone was already confirmed; for one that never was, an open shift comes
// back as part of THIS question (impact.open_shifts) instead.

export interface ZoneChangeAsk {
  from: string;
  to: string;
  message: string;
}

interface Body {
  code?: unknown;
  message?: unknown;
  timezone?: { from?: unknown; to?: unknown };
  impact?: { live_passes?: unknown; open_shifts?: unknown };
}

export const zoneChangeAsk = (error: unknown): ZoneChangeAsk | null => {
  const response = (error as { response?: { status?: number; data?: Body } })?.response;
  const data = response?.data;
  if (!data || response?.status !== 409 || data.code !== 'ZONE_CHANGE_NEEDS_CONFIRM') {
    return null;
  }

  const from = typeof data.timezone?.from === 'string' ? data.timezone.from : '';
  const to = typeof data.timezone?.to === 'string' ? data.timezone.to : '';
  if (!to) return null;

  const passes = Number(data.impact?.live_passes) || 0;
  // Only ever non-zero for a building whose zone was never confirmed: for any
  // other building an open shift is a refusal, not a question.
  const shifts = Number(data.impact?.open_shifts) || 0;
  const parts = [
    from ? `This moves the building from ${from} to ${to}.` : `This puts the building on ${to}.`,
    typeof data.message === 'string' ? data.message : '',
    passes > 0
      ? `${passes} visitor ${passes === 1 ? 'pass is' : 'passes are'} live at this building.`
      : '',
    shifts > 0
      ? `${shifts === 1 ? 'A shift is' : `${shifts} shifts are`} open here right now; ${shifts === 1 ? 'its' : 'their'} times will read on the new clock from this moment.`
      : '',
  ];

  return { from, to, message: parts.filter(Boolean).join(' ') };
};
